/**
 * Gesture configuration & validation utilities for the Hand Gesture Controls
 * feature (Phase 2).
 *
 * This module owns the gesture-to-action mapping, validation of custom
 * configurations, default config resolution, and per-tenant feature flag
 * resolution.
 *
 * Note: the shared `src/types/hand-gestures.ts` module also defines
 * `GestureAction`, `GestureMapping`, `GestureConfig`, and `FeatureFlagConfig`.
 * The Phase 2 tests import these types from THIS module to keep the validation
 * surface self-contained, so they are (re)defined here and kept structurally
 * compatible with the shared definitions.
 *
 * _Requirements: 2.2, 2.3, 2.4, 11.3, 12.1, 12.3, 12.4_
 */

/**
 * MediaPipe gesture category names accepted by the validator. `None` is the
 * no-hand sentinel and is never mapped to an action, but is included so that
 * the known-set is exhaustive with respect to the recognizer output.
 */
export const KNOWN_GESTURES = [
  'Open_Palm',
  'Thumb_Up',
  'Closed_Fist',
  'Victory',
  'Thumb_Down',
  'ILoveYou',
  'Pointing_Up',
  'None',
] as const;

/** Union of accepted MediaPipe gesture category names. */
export type KnownGesture = (typeof KNOWN_GESTURES)[number];

/** Actions that can be triggered by gestures. */
export type GestureAction =
  | 'next'
  | 'prev'
  | 'play'
  | 'pause'
  | 'toggleInstructions'
  | 'toggleUI';

/** A mapping from a gesture name to a viewer action. */
export interface GestureMapping {
  gesture: string;
  action: GestureAction;
}

/** A gesture configuration: the set of gesture-to-action mappings. */
export interface GestureConfig {
  mappings: GestureMapping[];
}

/** Result of validating a {@link GestureConfig}. */
export interface ValidationResult {
  /** True when there are no hard errors (duplicate actions). */
  valid: boolean;
  /** Hard errors that make the config invalid (e.g. duplicate actions). */
  errors: string[];
  /** Soft warnings (e.g. unknown gesture names that were ignored). */
  warnings: string[];
  /** Mappings that survived validation (known gestures, no duplicate action). */
  validMappings: GestureMapping[];
}

/**
 * Default gesture mapping (design decision C4):
 * - Thumb_Up      → play
 * - Closed_Fist   → pause
 * - Victory       → toggleInstructions
 * - Open_Palm     → toggleUI
 */
export const DEFAULT_GESTURE_CONFIG: GestureConfig = {
  mappings: [
    { gesture: 'Thumb_Up', action: 'play' },
    { gesture: 'Closed_Fist', action: 'pause' },
    { gesture: 'Victory', action: 'toggleInstructions' },
    { gesture: 'Open_Palm', action: 'toggleUI' },
  ],
};

/**
 * Validates a gesture configuration.
 *
 * - Duplicate actions (two or more gestures mapped to the same action) produce
 *   a hard error containing the word "duplicate" and make the config invalid.
 * - Unknown gesture names produce a warning naming the gesture and are excluded
 *   from `validMappings`, but do NOT make the config invalid.
 * - Known gestures whose action is not duplicated are retained in
 *   `validMappings`.
 */
export function validateGestureConfig(config: GestureConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const validMappings: GestureMapping[] = [];

  // Detect duplicate actions: group the gestures mapped to each action.
  const gesturesByAction = new Map<GestureAction, string[]>();
  for (const mapping of config.mappings) {
    const gestures = gesturesByAction.get(mapping.action) ?? [];
    gestures.push(mapping.gesture);
    gesturesByAction.set(mapping.action, gestures);
  }
  for (const [action, gestures] of gesturesByAction) {
    if (gestures.length > 1) {
      errors.push(
        `duplicate action "${action}" mapped to gestures: ${gestures.join(', ')}`,
      );
    }
  }

  // Filter unknown gestures (warning) and collect valid mappings.
  for (const mapping of config.mappings) {
    if ((KNOWN_GESTURES as readonly string[]).includes(mapping.gesture)) {
      validMappings.push(mapping);
    } else {
      warnings.push(`Unknown gesture "${mapping.gesture}" — ignored`);
    }
  }

  return { valid: errors.length === 0, errors, warnings, validMappings };
}

/** Returns the provided custom config, or the default when none is given. */
export function resolveGestureConfig(custom?: GestureConfig): GestureConfig {
  return custom ?? DEFAULT_GESTURE_CONFIG;
}

/** Feature flag configuration with optional per-tenant overrides. */
export interface FeatureFlagConfig {
  global: boolean;
  tenantOverrides: Record<string, boolean>;
}

/**
 * Resolves a feature flag for a tenant. Per-tenant overrides take precedence
 * over the global value; the global value is used when no override exists for
 * the queried tenant.
 */
export function resolveFeatureFlag(
  config: FeatureFlagConfig,
  tenantId: string,
): boolean {
  if (tenantId in config.tenantOverrides) {
    return config.tenantOverrides[tenantId];
  }
  return config.global;
}
